import { useState } from 'react';
import { loyaltyLevels } from '@/mocks/loyalty';

const LoyaltyLevels = () => {
  const [activeLevel, setActiveLevel] = useState('standard');
  const current = loyaltyLevels.find(l => l.id === activeLevel) ?? loyaltyLevels[0];

  return (
    <section className="py-20 px-4 md:px-8 lg:px-12" style={{ background: '#070710' }}>
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="text-center mb-12">
          <p className="font-mono text-xs tracking-[0.3em] mb-2" style={{ color: '#00f5ff' }}>УРОВНИ КАРТЫ</p>
          <h2 className="font-orbitron font-black text-3xl md:text-4xl text-white mb-3">
            ВЫБЕРИТЕ СВОЙ УРОВЕНЬ
          </h2>
          <p className="font-mono text-sm text-white/40 max-w-xl mx-auto">
            Два уровня привилегий с разными условиями начисления и списания баллов
          </p>
        </div>

        {/* Level switcher */}
        <div className="flex justify-center mb-10">
          <div className="flex gap-1 p-1 rounded-full" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            {loyaltyLevels.map(level => (
              <button
                key={level.id}
                onClick={() => setActiveLevel(level.id)}
                className="whitespace-nowrap flex items-center gap-2 px-6 py-3 rounded-full font-orbitron font-bold text-xs tracking-wider cursor-pointer transition-all duration-300"
                style={activeLevel === level.id
                  ? { background: level.color, color: '#050508', boxShadow: `0 0 20px ${level.glowColor}60` }
                  : { color: 'rgba(255,255,255,0.4)' }
                }
              >
                <i className={level.icon} />
                {level.name}
              </button>
            ))}
          </div>
        </div>

        {/* Cards comparison */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {loyaltyLevels.map(level => {
            const isActive = level.id === activeLevel;
            return (
              <div
                key={level.id}
                onClick={() => setActiveLevel(level.id)}
                className="rounded-2xl overflow-hidden cursor-pointer transition-all duration-500"
                style={{
                  border: `2px solid ${isActive ? level.borderColor : 'rgba(255,255,255,0.06)'}`,
                  background: isActive ? `${level.color}06` : 'rgba(255,255,255,0.02)',
                  boxShadow: isActive ? `0 0 40px ${level.glowColor}20` : 'none',
                  transform: isActive ? 'scale(1.01)' : 'scale(1)',
                }}
              >
                {/* Card header */}
                <div className="p-6 relative overflow-hidden" style={{ background: isActive ? `${level.color}10` : 'transparent' }}>
                  {isActive && (
                    <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-10" style={{ background: level.color, filter: 'blur(30px)', transform: 'translate(30%, -30%)' }} />
                  )}
                  <div className="flex items-start justify-between relative z-10">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-8 h-8 flex items-center justify-center rounded-xl" style={{ background: `${level.color}20`, border: `1px solid ${level.color}40` }}>
                          <i className={level.icon} style={{ color: level.color, fontSize: '16px' }} />
                        </div>
                        <h3 className="font-orbitron font-black text-xl" style={{ color: level.color, textShadow: isActive ? `0 0 15px ${level.glowColor}` : 'none' }}>
                          {level.name}
                        </h3>
                      </div>
                      <p className="font-mono text-xs text-white/40 ml-10">{level.subtitle}</p>
                    </div>
                    {isActive && (
                      <div className="px-3 py-1 rounded-full font-mono text-[10px] font-bold" style={{ background: level.color, color: '#050508' }}>
                        АКТИВЕН
                      </div>
                    )}
                  </div>

                  {/* Key metrics */}
                  <div className="grid grid-cols-3 gap-3 mt-5">
                    {[
                      { label: 'Кэшбэк', value: `${level.cashback}%`, icon: 'ri-coins-line' },
                      { label: 'Приветствие', value: `${level.welcomeBonus} б.`, icon: 'ri-gift-2-line' },
                      { label: 'День рождения', value: `${level.birthdayBonus} б.`, icon: 'ri-cake-line' },
                    ].map((m, i) => (
                      <div key={i} className="flex flex-col items-center p-3 rounded-xl text-center" style={{ background: `${level.color}08`, border: `1px solid ${level.color}15` }}>
                        <div className="w-5 h-5 flex items-center justify-center mb-1">
                          <i className={m.icon} style={{ color: level.color, fontSize: '14px' }} />
                        </div>
                        <div className="font-orbitron font-black text-base" style={{ color: level.color }}>{m.value}</div>
                        <div className="font-mono text-[9px] text-white/30 mt-0.5">{m.label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Benefits list */}
                <div className="p-6 pt-4">
                  <p className="font-mono text-[10px] tracking-widest text-white/30 mb-4">ПРЕИМУЩЕСТВА</p>
                  <ul className="space-y-3">
                    {level.benefits.map((b, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-6 h-6 flex items-center justify-center rounded-lg shrink-0 mt-0.5" style={{ background: `${level.color}15`, border: `1px solid ${level.color}25` }}>
                          <i className={b.icon} style={{ color: level.color, fontSize: '12px' }} />
                        </div>
                        <span className="font-mono text-xs text-white/60 leading-relaxed">
                          {b.highlight ? (
                            <>
                              {b.text.split(b.highlight)[0]}
                              <strong style={{ color: level.color }}>{b.highlight}</strong>
                              {b.text.split(b.highlight)[1]}
                            </>
                          ) : b.text}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {level.minSpend > 0 && (
                    <div className="mt-5 p-3 rounded-xl" style={{ background: `${level.color}08`, border: `1px solid ${level.color}20` }}>
                      <p className="font-mono text-[11px] text-white/50 leading-relaxed">
                        <i className="ri-information-line mr-1.5" style={{ color: level.color }} />
                        Присваивается при покупках от <strong style={{ color: level.color }}>{level.minSpend.toLocaleString()} ₽/год</strong> или участникам старше 55 лет
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footnotes */}
        <div className="mt-8 p-5 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <p className="font-mono text-[10px] text-white/25 leading-relaxed">
            * Статус пенсионера присваивается участникам старше 55 лет при регистрации карты или в мобильном приложении.<br />
            ** Приветственные баллы действуют в течение 30 дней с момента начисления на покупку от 500 ₽.<br />
            *** Баллы ко дню рождения начисляются только на карту с покупками. Действуют в течение 90 дней с момента начисления на покупку от 500 ₽.<br />
            **** Акция «С уважением к старшим» — каждую последнюю пятницу месяца для пенсионеров.
          </p>
        </div>
      </div>
    </section>
  );
};

export default LoyaltyLevels;
